// Wire-контракт Flex — единый источник (правило 1 «Single source of truth»,
// карточка flex-wire-contract-shared): PATCH-схема админ-редактирования,
// типы ответа админ-эндпоинта /admin/flex-orders и клиентский wire-shape
// serializeFlexOrderAsClient. Раньше эти пять копий жили врозь (apps/api
// flex.service + flexOrderSerializer, apps/admin useFlexOrders + FlexBody
// FIELD_KINDS, apps/client FlexOrderDetail) и расходились молча — теперь
// и сервер, и обе формы импортируют отсюда.
import { z } from "zod";
import { FLEX_TARIFF_CATEGORIES, } from "./index.js";
// ── PATCH /admin/flex-orders/:id ─────────────────────────────────────────────
// Per-place parcel parameters (FlexPlace in ./index.js). One entry per place
// (`pieces`); the admin measures weight/dimensions/crate per place, usually at
// the «Прибыло на склад» status. All measurements nullable — a place can be
// registered before it is fully measured.
export const flexPlaceSchema = z.object({
    weightKg: z.number().nonnegative().nullable(),
    // Dimensions are typed in by hand and optional (mandatory only when crated).
    lengthCm: z.number().int().positive().nullable(),
    widthCm: z.number().int().positive().nullable(),
    heightCm: z.number().int().positive().nullable(),
    crateNeeded: z.boolean(),
    // oversized (негабарит), довоз flag and note — optional so payloads written
    // before these fields still validate.
    oversized: z.boolean().optional(),
    additional: z.boolean().optional(),
    comment: z.string().nullable().optional(),
});
// Whitelisted patch fields. Status is intentionally NOT here — status changes
// must go through transitionFlexStatus so the ±1-step rule is enforced.
// Семантика значений: `null`/`""` = «человек очистил поле» (сервер пишет null);
// отсутствие ключа = «поле не трогали» (сервер не пишет ничего). Админ-форма
// (FlexBody) обязана слать ДЕЛЬТУ — только изменённые относительно снапшота
// поля, иначе поле, не пришедшее с сервера, затирается null-ом.
export const updateFlexOrderSchema = z
    .object({
    // ── Admin / processing ──
    pickupAddress: z.string().nullable(),
    deliveryAddress: z.string().nullable(),
    weightKg: z.number().positive().nullable(),
    declaredValueEur: z.number().nonnegative().nullable(),
    contents: z.string().nullable(),
    preorder: z.boolean(),
    customsRiskLevel: z.enum(["red", "yellow", "green"]).nullable(),
    dimensionsLengthCm: z.number().int().positive().nullable(),
    dimensionsWidthCm: z.number().int().positive().nullable(),
    dimensionsHeightCm: z.number().int().positive().nullable(),
    // ── Route ──
    originCountry: z.string().nullable(),
    destinationCountry: z.string().nullable(),
    // ── Sender ──
    senderFullName: z.string().nullable(),
    senderPhoneCode: z.string().nullable(),
    senderPhoneLocal: z.string().nullable(),
    senderPassport: z.string().nullable(),
    senderAddressLine: z.string().nullable(),
    // ── Recipient ──
    recipientFullName: z.string().nullable(),
    recipientCountry: z.string().nullable(),
    recipientCity: z.string().nullable(),
    recipientMissingPhone: z.boolean(),
    recipientMissingAddress: z.boolean(),
    recipientPhoneCode: z.string().nullable(),
    recipientPhoneLocal: z.string().nullable(),
    recipientMessenger: z.string().nullable(),
    recipientApartment: z.string().nullable(),
    recipientBuilding: z.string().nullable(),
    recipientStreet: z.string().nullable(),
    recipientZip: z.string().nullable(),
    recipientEmail: z.string().nullable(),
    recipientGeoUrl: z.string().nullable(),
    // ── Pickup ──
    selfDeliver: z.boolean(),
    pickupGeoUrl: z.string().nullable(),
    pickupCity: z.string().nullable(),
    pickupFloor: z.number().int().nullable(),
    pickupHasElevator: z.boolean().nullable(),
    pickupPhoneLocal: z.string().nullable(),
    pickupSameAsSender: z.boolean(),
    pickupDay: z.string().nullable(),
    pickupSlot: z.string().nullable(),
    // ── Забор: курьер/перевозчик (admin-internal) ──
    // pickupDate — редактируемая дата забора (ISO «YYYY-MM-DD»); конвертируется
    // в Date в updateFlexOrder. pickupCarrierRef — свободный идентификатор
    // перевозчика. carrierFeeGel — согласованная сумма его услуг.
    pickupDate: z.string().nullable(),
    pickupCarrierRef: z.string().nullable(),
    carrierFeeGel: z.number().nonnegative().nullable(),
    // ── Parcel / crate / storage ──
    pieces: z.number().int().positive().nullable(),
    crateNeeded: z.boolean(),
    crateCount: z.number().int().nonnegative(),
    storageNeeded: z.boolean(),
    storageUntil: z.string().nullable(),
    flexContentDescription: z.string().nullable(),
    // Per-place parameters; null clears all places.
    placesJson: z.array(flexPlaceSchema).nullable(),
    // «Довоз»: extra places added after the original handoff.
    additionalDelivery: z.boolean(),
    additionalPlacesCount: z.number().int().nonnegative(),
    // Tariff category for the auto-quote (Личные вещи / Коммерция).
    tariffCategory: z.enum(FLEX_TARIFF_CATEGORIES),
})
    .partial();
//# sourceMappingURL=wire.js.map